import { Box, CircularProgress } from "@mui/material";
import React, { useEffect, useState } from "react";
import { getVariables } from "../../../api/variables";
import { useFlowTestMeasurements } from "../../../hooks/FlowTest";
import { VariableResponse } from "../../../types/model/variable";
import { VARIABLE_CATEGORY_CODES } from "../../../types/model/variable/VariableCategories";
import FlowTestMeasurements from "../FlowTestMeasurements/FlowTestMeasurements";
import FlowTestMeasurementsChart from "../FlowTestMeasurements/FlowTestMeasurementsChart";

interface MeasurementsTabProps {
  flowTestId: string;
  captureId: number;
  entityId: number;
  handleError: (error: any) => void;
  canEdit: boolean;
}

const MeasurementsTab: React.FC<MeasurementsTabProps> = ({ flowTestId, captureId, entityId, handleError, canEdit }) => {
  const { 
    measurementsData, 
    isPending, 
    filters, 
    setFilters, 
    createMeasurement, 
    updateMeasurement, 
    deleteMeasurement,
    page,
    setPage,
    refetch
  } = useFlowTestMeasurements(flowTestId);
  const [isChartMaximized, setIsChartMaximized] = useState(false);
  const [variables, setVariables] = useState<VariableResponse[]>([]);

  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const data = await getVariables(false, VARIABLE_CATEGORY_CODES.FLOW_TEST_MEASUREMENT);
        setVariables(data);
      } catch (error) {
        handleError(error);
      }
    };
    fetchVariables();
  }, [handleError]);

  if (isPending && measurementsData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const toggleMapMaximization = () => {
    setIsChartMaximized((prev) => !prev);
  };

  return (
    <>
      <FlowTestMeasurements
        flowTestId={Number(flowTestId)}
        captureId={captureId}
        entityId={entityId}
        measurementsData={measurementsData}
        canEdit={canEdit}
        variables={variables}
        filters={filters}
        onApplyFilters={setFilters}
        onCreate={async (values) => {
          await createMeasurement(values).catch(handleError);
        }}
        onUpdate={async (id, values) => {
          await updateMeasurement({ measurementId: id, values }).catch(handleError);
        }}
        onDelete={async (id) => {
          await deleteMeasurement(id).catch(handleError);
        }}
        onRequestRefresh={refetch}
        isChartMaximized={isChartMaximized}
        toggleMapMaximization={toggleMapMaximization}
        page={page}
        onPageChange={setPage}
      />
      <FlowTestMeasurementsChart
        measurementsData={measurementsData}
        variables={variables}
        isChartMaximized={isChartMaximized}
        flowTestId={flowTestId}
        selectedVariableId={filters.variableId}
        xAxisType={filters.xAxis ?? "measuredAt"}
      />
    </>
  );
};

export default MeasurementsTab;
